import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import { PenLine, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GOLD = '#c9a96e';

function MetricItem({ icon: Icon, label, value, unit, color, theme }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: alpha(color, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={16} color={color} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2, color: theme.palette.text.primary }}
          >
            {value ?? '-'}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, fontSize: '0.68rem' }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const SignatureMetrics = ({ sigPerformance }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (sigPerformance.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ borderRadius: '8px', mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  const data = sigPerformance.data || {};

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: GOLD,
            mb: 1.5,
            letterSpacing: '0.02em',
          }}
        >
          {t('clmDashboard.signatureMetrics', 'Signature Metrics')}
        </Typography>

        <MetricItem
          icon={Clock}
          label={t('clmDashboard.avgTimeToSign', 'Avg. Time to Sign')}
          value={data.avg_days_to_sign}
          unit={t('clmDashboard.days', 'days')}
          color="#3b82f6"
          theme={theme}
        />

        <MetricItem
          icon={CheckCircle2}
          label={t('clmDashboard.completionRate', 'Completion Rate')}
          value={data.completion_rate}
          unit="%"
          color="#22c55e"
          theme={theme}
        />

        <MetricItem
          icon={PenLine}
          label={t('clmDashboard.pendingSignatures', 'Pending Signatures')}
          value={data.total_pending}
          color="#f59e0b"
          theme={theme}
        />

        <MetricItem
          icon={AlertTriangle}
          label={t('clmDashboard.stalledCount', 'Stalled (>3 days)')}
          value={data.stalled_signatures?.length ?? 0}
          color="#ef4444"
          theme={theme}
        />
      </CardContent>
    </Card>
  );
};

export default SignatureMetrics;
